/**
 * Small tags for leads lists: New, Unused, and performance (High FTD, High Profit, etc.).
 * All tags come from list-performance-tags API (performanceTags). Max 3 visible; rest in tooltip.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui';

const MAX_VISIBLE_TAGS_DEFAULT = 3;
const MAX_VISIBLE_TAGS_COMPACT = 2;

const PERFORMANCE_TAG_STYLES: Record<string, string> = {
  'High FTD': 'bg-success/15 text-success',
  'High Profit': 'bg-success/15 text-success',
  'Low Profit': 'bg-destructive/15 text-destructive',
  'High ROI': 'bg-primary/15 text-primary',
  'High Conv': 'bg-info/15 text-info',
  New: 'bg-primary/15 text-primary',
  Unused: 'bg-muted text-muted-foreground',
};

interface LeadsListTagsProps {
  /** Tags from list-performance-tags API (includes New, Unused, and custom rules). */
  performanceTags?: string[];
  /** Custom hex colors per tag (from list tag rules). Overrides default styles when set. */
  tagColors?: Record<string, string>;
  /** Compact single-line style; fewer visible tags, no wrap. */
  compact?: boolean;
  /** Disable expand tooltip when parent (e.g. TruncatedListCell) already has full hover. */
  disableTooltip?: boolean;
  /** Show a tiny spinner while tags are being fetched. */
  loading?: boolean;
  /** Cap the visible chips (rest fold into the +N pill) — e.g. 1 inside a
   *  closed select trigger, where the owner's name must stay readable. */
  maxVisible?: number;
  className?: string;
}

interface TagItem {
  label: string;
  style: string;
  customColor?: string;
}

export const LeadsListTags: FC<LeadsListTagsProps> = ({
  performanceTags = [],
  tagColors,
  compact = false,
  disableTooltip = false,
  loading = false,
  maxVisible: maxVisibleProp,
  className,
}) => {
  const perfTagItems: TagItem[] = (performanceTags ?? []).map((t) => {
    const customColor = tagColors?.[t];
    const style = customColor
      ? ''
      : (PERFORMANCE_TAG_STYLES[t] ?? 'bg-muted text-muted-foreground');
    return { label: t, style, customColor };
  });

  const allTags = perfTagItems;
  if (allTags.length === 0 && loading) {
    return <LoadingSpinner className="h-2.5 w-2.5 opacity-50" />;
  }
  if (allTags.length === 0) return null;

  const maxVisible =
    maxVisibleProp ?? (compact ? MAX_VISIBLE_TAGS_COMPACT : MAX_VISIBLE_TAGS_DEFAULT);
  const visible = allTags.slice(0, maxVisible);
  const hidden = allTags.slice(maxVisible);
  const hiddenCount = hidden.length;
  const content = (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1',
        compact ? 'flex-nowrap' : 'flex-wrap',
        compact && 'gap-0.5',
        className,
      )}
    >
      {visible.map((tag) => (
        <span
          key={tag.label}
          className={cn(
            'rounded px-1.5 py-0.5 text-[9px] font-medium',
            !tag.customColor && tag.style,
          )}
          style={tag.customColor ? { backgroundColor: `${tag.customColor}26`, color: tag.customColor } : undefined}
        >
          {tag.label}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          className="rounded px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground bg-muted/60"
          aria-label={`${hiddenCount} more tags`}
        >
          +{hiddenCount}
        </span>
      )}
    </span>
  );

  if (hidden.length > 0 && !disableTooltip) {
    return (
      <RadixTooltip.Provider delayDuration={100} skipDelayDuration={50}>
        <RadixTooltip.Root>
          <RadixTooltip.Trigger asChild>
            {content}
          </RadixTooltip.Trigger>
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              side="top"
              sideOffset={6}
              avoidCollisions
              collisionPadding={16}
              className="z-[300] max-w-[min(360px,90vw)] rounded-lg border border-border bg-popover px-3 py-2.5 text-popover-foreground shadow-lg"
            >
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider opacity-80">
                  All tags
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {allTags.map((tag) => (
                    <span
                      key={tag.label}
                      className={cn(
                        'rounded px-2 py-1 text-[10px] font-medium',
                        !tag.customColor && tag.style,
                      )}
                      style={tag.customColor ? { backgroundColor: `${tag.customColor}26`, color: tag.customColor } : undefined}
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    );
  }

  return content;
};
